/**
 * Directing the generator, from the browser's side.
 *
 * The same rule as `ChartTuning.ts`, and for the same reason: this file holds
 * the *shape* of a direction and none of its meaning. What a weight of 40 on
 * MANDALA does to a chart is decided once, in `generation/direction.py`, and a
 * second opinion here would be a second chart engine.
 *
 * What the browser does own is the vocabulary — which shapes exist, which
 * traversals exist, what a section is — because that is what a screen has to
 * render, and the honest place for "these are the choices" is next to the code
 * that draws them.
 */

/**
 * One stretch of song as the engine heard it.
 *
 * Measured, all of it. `label` is the section's intensity against the rest
 * of this song — LOW, NORMAL, HIGH, PEAK — and deliberately not VERSE or
 * CHORUS: the analysis cannot tell a quiet chorus from a loud bridge, and a
 * label that is wrong half the time is worse than one that is only ever what
 * it says.
 */
export interface DetectedSection {
  readonly index: number;
  readonly startTime: number;
  readonly endTime: number;
  readonly duration: number;
  readonly label: string;
  readonly energyMean: number;
  readonly energyVariance: number;
  readonly onsetDensity: number;
  readonly spectralFluxMean: number;
  readonly lowEnergy: number;
  readonly midEnergy: number;
  readonly highEnergy: number;
  readonly relativeIntensity: number;
  readonly changeConfidence: number;
  readonly beats: number;
}

export interface SectionAnalysis {
  readonly sections: readonly DetectedSection[];
  readonly boundaries: readonly number[];
}

/** Roles the engine may infer for a section. `UNKNOWN` is a normal answer. */
export const SEMANTIC_ROLES = [
  'INTRO',
  'VERSE',
  'PRE_CHORUS',
  'CHORUS',
  'BRIDGE',
  'BREAK',
  'DROP',
  'OUTRO',
  'INSTRUMENTAL',
  'UNKNOWN',
] as const;
export type SemanticRole = (typeof SEMANTIC_ROLES)[number];

/**
 * What the engine thinks a section *is*, kept apart from what it measured.
 *
 * Three kinds of number live here and the screen must never blur them:
 * `intensityLabel` is measured, `recurrenceGroup` and the activity figures are
 * computed from measurements, and `semanticRole` is inferred — which is why it
 * arrives with a confidence and why a low one reads `UNKNOWN`.
 */
export interface SectionSemantics {
  readonly index: number;
  readonly intensityLabel: string;
  readonly semanticRole: SemanticRole;
  readonly semanticRoleConfidence: number;
  readonly recurrenceGroup: number;
  readonly recurrenceStrength: number;
  readonly similarToSections: readonly number[];
  readonly melodicActivity: number;
  readonly harmonicActivity: number;
  readonly harmonicStability: number;
  readonly transientProfile: {
    readonly count: number;
    readonly dominant: string;
    readonly confidence: number;
    readonly shares: Readonly<Record<string, number>>;
  } | null;
  readonly evidence: readonly string[];
}

export interface SemanticAnalysis {
  readonly version: string;
  readonly sections: readonly SectionSemantics[];
  readonly phraseBoundaries: readonly number[];
}

/** Shapes a chunk of notes can be placed on. LEGACY is the original scatter. */
export const GEOMETRY_NAMES = [
  'LEGACY',
  'CIRCLE',
  'SPIRAL',
  'WAVE',
  'STAR',
  'RADIAL',
  'MANDALA',
] as const;
export type GeometryName = (typeof GEOMETRY_NAMES)[number];

/** Ways of walking a shape. */
export const PATTERN_NAMES = [
  'SEQUENTIAL',
  'REVERSE',
  'PING_PONG',
  'ALTERNATE',
  'OPPOSITE',
  'SKIP',
] as const;
export type PatternName = (typeof PATTERN_NAMES)[number];

/** Sound kinds the engine can currently tell apart. */
export const SOURCE_NAMES = ['beat', 'melody', 'ornament'] as const;
export type SourceName = (typeof SOURCE_NAMES)[number];

/** How many notes share one shape. */
export const CHUNK_SIZES = [4, 8, 16] as const;
export type ChunkSize = (typeof CHUNK_SIZES)[number];
export const DEFAULT_CHUNK_SIZE: ChunkSize = 8;

export type WeightMap = Readonly<Record<string, number>>;

export interface GeometryShapeSpec {
  readonly pointCount: number;
  readonly rotation: number;
  readonly direction: 1 | -1;
  readonly mirror: boolean;
  readonly scale: number;
  readonly startIndex: number;
}

export const DEFAULT_SHAPE: GeometryShapeSpec = Object.freeze({
  pointCount: 12,
  rotation: 0,
  direction: 1,
  mirror: false,
  scale: 1,
  startIndex: 0,
});

/**
 * One stretch of the song with its own direction.
 *
 * A section is a *region plus intentions*, never a list of notes. Empty
 * intentions are legal: marking a chorus before deciding what to do with it is
 * a real step in using this.
 */
export interface DirectionSection {
  readonly startTime: number;
  readonly endTime: number;
  readonly label: string;
  readonly sourceWeights: WeightMap;
  readonly geometryWeights: WeightMap;
  readonly patternWeights: WeightMap;
  readonly tuningOverrides: WeightMap;
  /**
   * This section's own shape parameters, over the global ones.
   *
   * Empty for every direction a player wrote by hand and for every direction
   * that predates the field — which is what keeps those producing the charts
   * they always produced.
   */
  readonly geometryParameters?: WeightMap;
}

export interface ChartDirection {
  readonly sourceWeights: WeightMap;
  readonly geometryWeights: WeightMap;
  readonly patternWeights: WeightMap;
  readonly chunkSize: ChunkSize;
  readonly geometry: GeometryShapeSpec;
  readonly sections: readonly DirectionSection[];
  /**
   * Musical landmarks the engine found, carried as plain times.
   *
   * The browser never reads them and never writes them — it holds them and
   * hands them back, so that regenerating one section of a semantically
   * directed pack still knows where the phrases and the chord changes were.
   * Absent on every direction that did not come from that path.
   */
  readonly phraseBoundaries?: readonly number[];
  readonly harmonicChanges?: readonly number[];
  readonly melodicMoments?: readonly number[];
}

export const EMPTY_DIRECTION: ChartDirection = Object.freeze({
  sourceWeights: Object.freeze({}),
  geometryWeights: Object.freeze({}),
  patternWeights: Object.freeze({}),
  chunkSize: DEFAULT_CHUNK_SIZE,
  geometry: DEFAULT_SHAPE,
  sections: Object.freeze([]),
});

/** Source weights where nothing is being biased. Sent as absent, not as ones. */
export const NEUTRAL_SOURCE_WEIGHTS: Readonly<Record<SourceName, number>> = Object.freeze({
  beat: 1,
  melody: 1,
  ornament: 1,
});

function hasWeight(weights: WeightMap, ignore: readonly string[] = []): boolean {
  return Object.entries(weights).some(([name, value]) => value > 0 && !ignore.includes(name));
}

/**
 * Whether anything here would change the automatic chart.
 *
 * The distinction matters more than it looks: a direction that names only
 * LEGACY, or that leaves every source at 1, is a direction to carry on, and it
 * must travel as *nothing* so the engine stays on the path it has always taken.
 */
export function directsAnything(direction: ChartDirection): boolean {
  const sourcesBiased = SOURCE_NAMES.some(
    (name) => (direction.sourceWeights[name] ?? 1) < 1 - 1e-9,
  );
  const shaped = hasWeight(direction.geometryWeights, ['LEGACY']);
  const sectioned = direction.sections.length > 0;
  return sourcesBiased || shaped || sectioned;
}

/**
 * The document the engine reads, or `null` when nothing was directed.
 *
 * Neutral source weights are dropped rather than sent as ones: an engine told
 * "keep everything" does the same thing as an engine told nothing, and the
 * quieter request is the one that cannot drift.
 */
export function toWire(direction: ChartDirection): Record<string, unknown> | null {
  if (!directsAnything(direction)) return null;
  const sources: Record<string, number> = {};
  for (const name of SOURCE_NAMES) {
    const weight = direction.sourceWeights[name] ?? 1;
    if (weight < 1 - 1e-9) sources[name] = Number(weight.toFixed(4));
  }
  const geometry: Record<string, number> = {};
  for (const [name, weight] of Object.entries(direction.geometryWeights)) {
    if (weight > 0) geometry[name] = weight;
  }
  const patterns: Record<string, number> = {};
  for (const [name, weight] of Object.entries(direction.patternWeights)) {
    if (weight > 0) patterns[name] = weight;
  }
  return {
    sourceWeights: sources,
    geometryWeights: geometry,
    patternWeights: patterns,
    chunkSize: direction.chunkSize,
    // The shape's own parameters travel only when a shape was asked for; an
    // engine reading them without one would apply them to nothing.
    ...(Object.keys(geometry).length === 0 ? {} : { geometry: direction.geometry }),
    sections: direction.sections.map((section) => ({
      // Sent exactly as they were received. Rounding a boundary at all — even
      // to a tenth of a millisecond — can push the event sitting on it into
      // the neighbouring section, which changes the chunk it belongs to and
      // the shape it lands on, and makes a chart that should have round-tripped
      // unchanged come back subtly different. Boundaries are rounded where
      // they are read, on screen, and nowhere else.
      startTime: section.startTime,
      endTime: section.endTime,
      label: section.label,
      sourceWeights: section.sourceWeights,
      geometryWeights: section.geometryWeights,
      patternWeights: section.patternWeights,
      tuningOverrides: section.tuningOverrides,
      // Sent back exactly as it arrived, or not at all. A section the engine
      // shaped keeps its shape through a regeneration; one the player drew
      // stays the document it has always been.
      ...(section.geometryParameters === undefined ||
      Object.keys(section.geometryParameters).length === 0
        ? {}
        : { geometryParameters: section.geometryParameters }),
    })),
    ...(direction.phraseBoundaries === undefined || direction.phraseBoundaries.length === 0
      ? {}
      : { phraseBoundaries: direction.phraseBoundaries }),
    ...(direction.harmonicChanges === undefined || direction.harmonicChanges.length === 0
      ? {}
      : { harmonicChanges: direction.harmonicChanges }),
    ...(direction.melodicMoments === undefined || direction.melodicMoments.length === 0
      ? {}
      : { melodicMoments: direction.melodicMoments }),
  };
}

/**
 * The engine's own direction, read back into the browser's shape.
 *
 * DIRECTED AUTO writes a direction the player never typed, and both things
 * that follow need it in hand: the panel has to show what was decided, and a
 * later "regenerate this section" has to hand the engine the direction the
 * pack was actually built with. Without it the baseline would be whatever
 * the panel happened to hold, and every other section would move.
 */
export function directionFromWire(raw: unknown): ChartDirection | null {
  if (raw === null || typeof raw !== 'object') return null;
  const source = raw as Record<string, unknown>;
  const weights = (value: unknown): WeightMap => {
    if (value === null || typeof value !== 'object') return {};
    const out: Record<string, number> = {};
    for (const [name, weight] of Object.entries(value as Record<string, unknown>)) {
      if (typeof weight === 'number' && Number.isFinite(weight)) out[name] = weight;
    }
    return out;
  };
  const shapeRaw = (source['geometry'] ?? {}) as Record<string, unknown>;
  const shape: GeometryShapeSpec = {
    pointCount: typeof shapeRaw['pointCount'] === 'number' ? shapeRaw['pointCount'] : DEFAULT_SHAPE.pointCount,
    rotation: typeof shapeRaw['rotation'] === 'number' ? shapeRaw['rotation'] : 0,
    direction: shapeRaw['direction'] === -1 ? -1 : 1,
    mirror: shapeRaw['mirror'] === true,
    scale: typeof shapeRaw['scale'] === 'number' ? shapeRaw['scale'] : 1,
    startIndex: typeof shapeRaw['startIndex'] === 'number' ? shapeRaw['startIndex'] : 0,
  };
  const rawSections = Array.isArray(source['sections']) ? source['sections'] : [];
  const sections: DirectionSection[] = [];
  for (const entry of rawSections) {
    if (entry === null || typeof entry !== 'object') continue;
    const item = entry as Record<string, unknown>;
    const startTime = typeof item['startTime'] === 'number' ? item['startTime'] : 0;
    const endTime = typeof item['endTime'] === 'number' ? item['endTime'] : 0;
    if (endTime <= startTime) continue;
    sections.push({
      startTime,
      endTime,
      label: typeof item['label'] === 'string' ? item['label'] : '',
      sourceWeights: weights(item['sourceWeights']),
      geometryWeights: weights(item['geometryWeights']),
      patternWeights: weights(item['patternWeights']),
      tuningOverrides: weights(item['tuningOverrides']),
      ...(item['geometryParameters'] === undefined
        ? {}
        : { geometryParameters: weights(item['geometryParameters']) }),
    });
  }
  const times = (value: unknown): readonly number[] =>
    Array.isArray(value) ? value.filter((entry): entry is number => typeof entry === 'number') : [];
  const phrases = times(source['phraseBoundaries']);
  const harmonics = times(source['harmonicChanges']);
  const melodics = times(source['melodicMoments']);
  const chunk = source['chunkSize'];
  return {
    sourceWeights: weights(source['sourceWeights']),
    geometryWeights: weights(source['geometryWeights']),
    patternWeights: weights(source['patternWeights']),
    chunkSize: (CHUNK_SIZES as readonly number[]).includes(chunk as number)
      ? (chunk as ChunkSize)
      : DEFAULT_CHUNK_SIZE,
    geometry: shape,
    sections,
    ...(phrases.length === 0 ? {} : { phraseBoundaries: phrases }),
    ...(harmonics.length === 0 ? {} : { harmonicChanges: harmonics }),
    ...(melodics.length === 0 ? {} : { melodicMoments: melodics }),
  };
}

/** A new section covering a span, with nothing decided about it yet. */
/**
 * The detected sections, as sections a player can then direct.
 *
 * Copied rather than referenced: what the engine heard is a measurement and
 * stays one, while what the player does with it is theirs to change.
 */
export function sectionsFromAnalysis(analysis: SectionAnalysis): DirectionSection[] {
  return analysis.sections.map((section) => ({
    startTime: section.startTime,
    endTime: section.endTime,
    label: `${section.label}-${section.index}`,
    sourceWeights: {},
    geometryWeights: {},
    patternWeights: {},
    tuningOverrides: {},
  }));
}

export function emptySection(startTime: number, endTime: number, label = ''): DirectionSection {
  return {
    startTime,
    endTime,
    label,
    sourceWeights: {},
    geometryWeights: {},
    patternWeights: {},
    tuningOverrides: {},
  };
}

/**
 * Sections in order, with overlaps trimmed the way the engine trims them.
 *
 * Doing it here as well is not duplication of *meaning* — the engine still
 * decides — it is so the list a player is looking at matches the chart they are
 * about to get. A section that will be trimmed to nothing is dropped, because
 * showing a row that has no effect is worse than showing none.
 */
export function tidySections(sections: readonly DirectionSection[]): DirectionSection[] {
  const ordered = [...sections].sort(
    (a, b) => a.startTime - b.startTime || a.endTime - b.endTime,
  );
  const out: DirectionSection[] = [];
  for (const section of ordered) {
    const previous = out[out.length - 1];
    if (previous !== undefined && section.startTime < previous.endTime) {
      const startTime = previous.endTime;
      if (startTime >= section.endTime) continue;
      out.push({ ...section, startTime });
      continue;
    }
    out.push(section);
  }
  return out;
}

/** Percentages for display: weights normalised to a hundred. */
export function asPercentages(weights: WeightMap): Record<string, number> {
  const total = Object.values(weights).reduce((sum, value) => sum + Math.max(0, value), 0);
  if (total <= 0) return {};
  const out: Record<string, number> = {};
  for (const [name, value] of Object.entries(weights)) {
    if (value > 0) out[name] = Math.round((value / total) * 100);
  }
  return out;
}
